import { SimplePagination, Table } from '@dixa/lib-shared-habla';
import { usePrevious, useUpdateEffect } from 'react-use';

import { numbersApi } from 'apis/numbers';
import { ratingApi } from 'apis/rating';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import { SelectSurveyModal } from '../../../survey/components/select/selectSurveyModal';

import { PhoneContactPointHeadRow } from './components/phoneSurveysTableHeadRow/phoneSurveysTableHeadRow';
import { PhoneSurveyTableRow } from './components/phoneSurveysTableRow/phoneSurveysTableRow';

/**
 * Represents a component that displays a table with the phone numbers with
 * survey links.
 */
export const PhoneSurveysTable = (): JSX.Element => {
  const {
    surveyLinks,
    isLoading: isLoadingSurveyLinks,
    refetch: refetchSurveyLinks,
  } = ratingApi.useReadRatingSettingsLinksQuery(
    { contactPointKind: 'phone_number' },
    {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        surveyLinks: data?.data.rating_settings_links ?? [],
      }),
    },
  );

  const {
    items: phoneNumbers,
    asyncStatus: phoneNumbersAsyncStatus,
    hasNextPage,
    hasPreviousPage,
    nextPage,
    previousPage,
  } = usePaginatedEndpoint({
    endpoint: numbersApi.endpoints.readNumbers,
    initialArg: { limit: 12 },
    itemsSelector: ({ data }) => data.numbers,
  });

  const readSurveysQueryState =
    ratingApi.endpoints.readRatingSettings.useQueryState({
      level: 'survey',
    });
  const prevReadSurveysQueryState = usePrevious(readSurveysQueryState);

  // Handles refetching the survey links when the surveys data has been updated, e.g. in the
  // situation where a survey is deleted where the survey link should be removed.
  useUpdateEffect(() => {
    if (prevReadSurveysQueryState?.data) {
      refetchSurveyLinks();
    }
  }, [readSurveysQueryState.data]);

  const isLoading =
    isLoadingSurveyLinks || phoneNumbersAsyncStatus === 'pending';

  return (
    <>
      <Table loading={isLoading} loadingStrategy="disabled" stickyHeader>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Number</Table.Th>
            <Table.Th>Name</Table.Th>
            <Table.Th>Survey</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th
              tooltip="For numbers connected to SMS surveys, the text message
                      will display your Sender Name/ID to the customer instead
                      of the phone number.
                      Not available in all countries."
            >
              Sender name
            </Table.Th>
            <Table.Th width="0">Enabled</Table.Th>
            <Table.Th width="0"></Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {isLoading ? (
            <PhoneContactPointHeadRow />
          ) : (
            phoneNumbers.map((phoneNumber) => (
              <PhoneSurveyTableRow
                key={phoneNumber.e164_format}
                phoneNumber={phoneNumber}
                link={surveyLinks.find(
                  (link) => link.contact_point === phoneNumber.e164_format,
                )}
                onLinkChange={refetchSurveyLinks}
                isSenderNameVisible
              />
            ))
          )}
        </Table.Tbody>

        <Table.Footer>
          <SimplePagination
            nextButtonClick={nextPage}
            nextButtonDisabled={!hasNextPage}
            previousButtonClick={previousPage}
            previousButtonDisabled={!hasPreviousPage}
          />
        </Table.Footer>
      </Table>

      <SelectSurveyModal />
    </>
  );
};
